/**
 * @module-isolated analytics-asistente
 * Asistente de ayuda Analytics (fase 1: métricas / pantallas).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchAnalyticsAsistente } from '../../services/analytics-api.js';
import { getIdToken } from '../../services/firebase-auth.js';

const MAX_HIST = 8;
const STORAGE_KEY = 'analytics:asistente:hist:v1';

const CHIPS = [
  {
    label: 'Meta vs Kommo',
    text: '¿Qué diferencia hay entre Clientes potenciales (Meta) y En Kommo (match)?'
  },
  {
    label: 'Alianzas',
    text: '¿Cómo se miden los leads de PatagoLand o Euro sin costo Meta?'
  },
  {
    label: 'Camino reunión',
    text: 'En el camino por campaña, ¿qué significan reuniones, minuta, propuesta y reserva?'
  },
  {
    label: 'Período',
    text: '¿Qué filtra el período de fechas en Leads y el camino por campaña?'
  }
];

function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatReply(text) {
  return escapeHtml(text || '')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\n/g, '<br>');
}

function loadHistory() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(-MAX_HIST).map((item) => ({
      role: item.role === 'assistant' ? 'assistant' : 'user',
      text: String(item.text || '').slice(0, 600)
    }));
  } catch (e) {
    return [];
  }
}

function saveHistory(history) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(-MAX_HIST)));
  } catch (e) {
    /* ignore */
  }
}

export default function AnalyticsAssistant() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState('');
  const [history, setHistory] = useState(() => loadHistory());
  const [error, setError] = useState('');
  const listRef = useRef(null);

  const routeId = useMemo(() => {
    const p = String(location.pathname || '');
    return p.replace(/^\/+/, '') || 'inicio';
  }, [location.pathname]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, history, busy]);

  const ask = useCallback(
    async (rawText) => {
      const pregunta = String(rawText || '').trim();
      if (!pregunta || busy) return;
      setError('');
      setBusy(true);
      const nextHist = history.concat([{ role: 'user', text: pregunta }]);
      setHistory(nextHist);
      setInput('');
      try {
        const token = await getIdToken(false);
        const res = await fetchAnalyticsAsistente(
          {
            pregunta,
            historial: history.slice(-4),
            contexto: {
              routeId,
              hint: 'Fase 1 ayuda métricas Analytics'
            }
          },
          token
        );
        const answer =
          (res && (res.respuesta || res.answer || res.text)) ||
          'No recibí respuesta del asistente.';
        const withBot = nextHist.concat([{ role: 'assistant', text: String(answer) }]);
        setHistory(withBot);
        saveHistory(withBot);
        if (res && res.mode === 'fallback' && res.llmError) {
          setError('Guía local (LLM: ' + String(res.llmError).slice(0, 80) + ')');
        }
      } catch (err) {
        const msg = (err && err.message) || 'No pude consultar al asistente.';
        setError(msg);
        const withBot = nextHist.concat([
          {
            role: 'assistant',
            text:
              'No pude consultar al asistente (' +
              msg +
              '). Revisá sesión o GEMINI_API_KEY en analytics-api.'
          }
        ]);
        setHistory(withBot);
        saveHistory(withBot);
      } finally {
        setBusy(false);
      }
    },
    [busy, history, routeId]
  );

  return (
    <div className={'ana-aa-root' + (open ? ' is-open' : '')}>
      <button
        type="button"
        className="ana-aa-fab"
        aria-expanded={open}
        aria-controls="ana-aa-panel"
        title="Ayuda Analytics"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ana-aa-fab-label">{open ? 'Cerrar' : 'Ayuda'}</span>
        <span className="ana-aa-fab-sub">Analytics</span>
      </button>

      {open ? (
        <section id="ana-aa-panel" className="ana-aa-panel" aria-label="Asistente Analytics">
          <header className="ana-aa-head">
            <div>
              <p className="ana-aa-title">Ayuda Analytics</p>
              <p className="ana-aa-sub">Métricas, caminos Meta/alianzas y pantallas</p>
            </div>
            <button type="button" className="ana-aa-close" onClick={() => setOpen(false)}>
              ×
            </button>
          </header>

          <div className="ana-aa-chips" role="list">
            {CHIPS.map((c) => (
              <button
                key={c.label}
                type="button"
                className="ana-aa-chip"
                disabled={busy}
                onClick={() => ask(c.text)}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="ana-aa-messages" ref={listRef}>
            {!history.length ? (
              <p className="ana-aa-empty">
                Preguntá qué significa un KPI o cómo leer el camino por campaña / alianza.
              </p>
            ) : null}
            {history.map((m, i) => (
              <div
                key={i}
                className={
                  'ana-aa-bubble' + (m.role === 'assistant' ? ' is-bot' : ' is-user')
                }
              >
                {m.role === 'assistant' ? (
                  <div dangerouslySetInnerHTML={{ __html: formatReply(m.text) }} />
                ) : (
                  m.text
                )}
              </div>
            ))}
            {busy ? <p className="ana-aa-typing">Pensando…</p> : null}
          </div>

          {error ? <p className="ana-aa-error">{error}</p> : null}

          <form
            className="ana-aa-form"
            onSubmit={(ev) => {
              ev.preventDefault();
              ask(input);
            }}
          >
            <input
              type="text"
              className="ana-aa-input"
              value={input}
              disabled={busy}
              maxLength={600}
              placeholder="Ej. Meta vs Kommo match…"
              onChange={(ev) => setInput(ev.target.value)}
            />
            <button type="submit" className="ana-aa-send" disabled={busy || !input.trim()}>
              Enviar
            </button>
          </form>
        </section>
      ) : null}
    </div>
  );
}
